import React, { useEffect, useState } from 'react';
import { Alert, Typography, Spin, Progress } from 'antd';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Paragraph, Text } = Typography;

/**
 * Component for displaying background task progress notifications
 * 
 * @param {string} taskId - The ID of the task to track
 * @param {string} title - Title to display for the task
 * @param {function} onComplete - Callback function when task completes
 * @param {boolean} autoHide - Whether to auto-hide when complete
 */
const TaskNotification = ({ taskId, title = "处理任务中", onComplete = null, autoHide = true }) => {
  const [status, setStatus] = useState('loading'); // loading, running, completed, failed
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [visible, setVisible] = useState(true);
  
  // Function to fetch task status
  const fetchTaskStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/tasks/status/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const taskStatus = response.data.status;
      setStatus(taskStatus.status);
      setMessage(taskStatus.message);
      
      if (taskStatus.result) {
        setResult(taskStatus.result);
      }
      
      // If task is complete, call the onComplete callback
      if (taskStatus.status === 'completed' && onComplete) {
        onComplete(taskStatus.result);
        
        // Auto-hide after 5 seconds if autoHide is true
        if (autoHide) {
          setTimeout(() => {
            setVisible(false);
          }, 5000);
        }
      }
      
      // If task is still running, schedule another fetch
      if (taskStatus.status === 'running' || taskStatus.status === 'queued') {
        setTimeout(fetchTaskStatus, 3000); // Check again in 3 seconds
      }
    } catch (error) {
      console.error('Error fetching task status:', error);
      setStatus('failed');
      setMessage('获取任务状态失败');
    }
  };
  
  // Initial fetch on component mount
  useEffect(() => {
    if (taskId) {
      fetchTaskStatus();
    }
    
    // Clean up
    return () => {
      // Cancel any pending timeouts if component unmounts
    };
  }, [taskId]);
  
  // Don't render if not visible
  if (!visible) {
    return null;
  }
  
  // Get status type for Alert component
  const getAlertType = () => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'warning';
    }
  };
  
  // Get icon based on status
  const getIcon = () => {
    if (status === 'running' || status === 'queued') {
      return <LoadingOutlined spin />;
    } else if (status === 'completed') {
      return <CheckCircleOutlined />;
    } else if (status === 'failed') {
      return <CloseCircleOutlined />;
    }
    return <InfoCircleOutlined />;
  };
  
  // Show progress if task is running
  const getProgressIndicator = () => {
    if (status === 'running' || status === 'queued') {
      return (
        <Progress 
          percent={status === 'running' ? 50 : 10} 
          status="active" 
          showInfo={false} 
          style={{ marginTop: 10 }}
        />
      );
    }
    return null;
  };
  
  return (
    <Alert
      type={getAlertType()}
      message={title}
      description={
        <div>
          <Paragraph>{message}</Paragraph>
          
          {result && result.success_count !== undefined && (
            <Paragraph>
              <Text strong>订阅成功:</Text> {result.success_count} 个号码 | 
              <Text strong> 失败:</Text> {result.failed_count} 个号码
            </Paragraph>
          )}
          
          {getProgressIndicator()}
        </div>
      }
      icon={getIcon()}
      showIcon
      style={{ marginBottom: 16 }}
    />
  );
};

export default TaskNotification; 